import {
  cosFromDegrees,
  sinFromDegrees,
  precisionRound,
} from '@crystal-eyes/lib/math'
import { DiscType, CircumplexVector } from '@crystal-eyes/types'

export function DistanceBetweenCircumplexVectors(
  vecOne: CircumplexVector,
  vecTwo: CircumplexVector,
) {
  const [pointOne, pointTwo] = [vecOne, vecTwo].map(vectorToCoordinates)
  const x = Math.abs(pointOne.x - pointTwo.x)
  const y = Math.abs(pointOne.y - pointTwo.y)

  return Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2))
}

function vectorToCoordinates(vector: CircumplexVector) {
  return {
    x: cosFromDegrees(vector.degrees) * vector.intensity,
    y: sinFromDegrees(vector.degrees) * vector.intensity,
  }
}

export function circumplexPoint(degrees: number, intensity: number) {
  const length = intensity / 2
  const centerX = 50
  const centerY = 50

  const calculatedX = cosFromDegrees(degrees) * length
  const calculatedY = sinFromDegrees(degrees) * length
  return [
    precisionRound(calculatedX + centerX, 2),
    precisionRound(calculatedY + centerY, 2),
  ]
}

const BOUNDARIES: { [key in DiscType]: number[] } = {
  [DiscType.IS]: [350, 11],
  [DiscType.Is]: [12, 34],
  [DiscType.I]: [35, 56],
  [DiscType.Id]: [57, 79],
  [DiscType.DI]: [80, 101],
  [DiscType.Di]: [102, 124],
  [DiscType.D]: [125, 146],
  [DiscType.Dc]: [147, 169],
  [DiscType.CD]: [170, 191],
  [DiscType.Cd]: [192, 214],
  [DiscType.C]: [215, 236],
  [DiscType.Cs]: [237, 259],
  [DiscType.SC]: [260, 281],
  [DiscType.Sc]: [282, 304],
  [DiscType.S]: [305, 326],
  [DiscType.Si]: [327, 349],
}

const ADJACENT_DISC_TYPES = {
  [DiscType.IS]: [DiscType.Si, DiscType.Is],
  [DiscType.Is]: [DiscType.IS, DiscType.I],
  [DiscType.I]: [DiscType.Is, DiscType.Id],
  [DiscType.Id]: [DiscType.I, DiscType.DI],
  [DiscType.DI]: [DiscType.Id, DiscType.Di],
  [DiscType.Di]: [DiscType.DI, DiscType.D],
  [DiscType.D]: [DiscType.Di, DiscType.Dc],
  [DiscType.Dc]: [DiscType.D, DiscType.CD],
  [DiscType.CD]: [DiscType.Dc, DiscType.Cd],
  [DiscType.Cd]: [DiscType.CD, DiscType.C],
  [DiscType.C]: [DiscType.Cd, DiscType.Cs],
  [DiscType.Cs]: [DiscType.C, DiscType.SC],
  [DiscType.SC]: [DiscType.Cs, DiscType.Sc],
  [DiscType.Sc]: [DiscType.SC, DiscType.S],
  [DiscType.S]: [DiscType.Sc, DiscType.Si],
  [DiscType.Si]: [DiscType.S, DiscType.IS],
}

const OPPOSITE_DISC_TYPES = {
  [DiscType.IS]: DiscType.CD,
  [DiscType.Is]: DiscType.Cd,
  [DiscType.I]: DiscType.C,
  [DiscType.Id]: DiscType.Cs,
  [DiscType.DI]: DiscType.SC,
  [DiscType.Di]: DiscType.Sc,
  [DiscType.D]: DiscType.S,
  [DiscType.Dc]: DiscType.Si,
  [DiscType.CD]: DiscType.IS,
  [DiscType.Cd]: DiscType.Is,
  [DiscType.C]: DiscType.I,
  [DiscType.Cs]: DiscType.Id,
  [DiscType.SC]: DiscType.DI,
  [DiscType.Sc]: DiscType.Di,
  [DiscType.S]: DiscType.D,
  [DiscType.Si]: DiscType.Dc,
}

export function degreesToDiscType(degrees: number): DiscType | null {
  if (degrees <= BOUNDARIES.IS[1]) return DiscType.IS
  if (degrees >= BOUNDARIES.IS[0]) return DiscType.IS
  const deg = degrees % 360

  let discType: DiscType | null = null
  Object.keys(BOUNDARIES).forEach(discTypeStr => {
    const currDiscType = discTypeStr as DiscType
    if (
      deg >= BOUNDARIES[currDiscType as DiscType][0] &&
      deg <= BOUNDARIES[currDiscType][1]
    ) {
      discType = currDiscType
    }
  })

  return discType
}

function degreesFromDiscTypeCenter(
  discType: DiscType,
  degrees: number,
): number {
  if (degrees < BOUNDARIES.IS[1]) {
    return degrees
  } else if (degrees > BOUNDARIES.IS[0]) {
    return 360 - degrees
  }

  const boundaries = BOUNDARIES[discType]
  const midpoint = (boundaries[0] + boundaries[1]) / 2.0

  return midpoint - degrees
}

export function circumplexToFourPercentage(
  discType: DiscType,
  degrees: number,
  intensity: number,
) {
  const scores: { [key: string]: number } = {
    [DiscType.D]: 0.0,
    [DiscType.I]: 0.0,
    [DiscType.S]: 0.0,
    [DiscType.C]: 0.0,
  }

  // Start from baseline, textual DISC Type for sanity
  if (discType.length === 1) {
    scores[discType] = 100.0
  } else {
    scores[discType[0].toUpperCase()] = 72.0
    scores[discType[1].toUpperCase()] = 28.0
  }

  // Adjust based on distance from center of type
  const degreesFromCenter = degreesFromDiscTypeCenter(discType, degrees)
  const lean: DiscType =
    degreesFromCenter > 0
      ? ADJACENT_DISC_TYPES[discType][0]
      : ADJACENT_DISC_TYPES[discType][1]

  const maxLean = 11
  const leanModifier = degreesFromCenter / maxLean

  if (lean.length === 1) {
    scores[lean.toUpperCase()] += 20.0 * leanModifier
  } else {
    scores[lean[0].toUpperCase()] += 20.0 * leanModifier
    scores[lean[1].toUpperCase()] += 10.0 * leanModifier
  }

  // Adjust for intensity
  const maxIntensity = 100.0
  const intensityModifier = (maxIntensity - intensity) * 0.2
  const oppositeDiscType = OPPOSITE_DISC_TYPES[discType]

  if (oppositeDiscType.length === 1) {
    scores[oppositeDiscType.toUpperCase()] += intensityModifier
  } else {
    scores[oppositeDiscType[0].toUpperCase()] += intensityModifier * 0.5
    scores[oppositeDiscType[1].toUpperCase()] += intensityModifier * 0.5
  }

  // Softmax
  let total = 0.0
  Object.keys(scores).forEach(discChar => {
    total += scores[discChar]
  })

  Object.keys(scores).forEach(discChar => {
    scores[discChar] = scores[discChar] / total
  })

  return scores
}
